/**
 * Нижние листы пула на карточке байка: «Поставил» и «Убрать из пула».
 *
 * Листы, а не экраны — то же правило §2.2, что у «Замечания диспетчеру»:
 * водитель стоит у байка, и контекст карточки (номер, замок) не должен
 * пропадать ради одного выбора.
 */
import { useState } from 'react'
import type { LatLng } from '@/lib/geo'
import {
  DROP_COMMENT_MAX,
  deliverPayload,
  deliverResultLabel,
  dropPayload,
  dropReasonLabel,
  type DeliverResult,
  type DropReason,
} from '@/lib/pool'
import { BigButton, BottomSheet, Notice } from '@/components/ui'

const DELIVER_RESULTS: readonly DeliverResult[] = ['line', 'charge', 'broken']

/** Что станет с байком — называется до нажатия, а не обнаруживается потом. */
const DELIVER_HINT: Record<DeliverResult, string> = {
  line: 'байк встанет в линию там, где вы стоите',
  charge: 'байк уйдёт «На базу»',
  broken: 'байк станет «Сломан»',
}

export function DeliverSheet({
  open,
  onClose,
  me,
  onDeliver,
  destination,
}: {
  open: boolean
  onClose: () => void
  me: LatLng | null
  onDeliver: (payload: Record<string, unknown>) => void
  /** Куда этот байк везли: «выгрузка» — по умолчанию «На линию». */
  destination?: string | undefined
}) {
  const unload = destination === 'unload'
  return (
    <BottomSheet open={open} onClose={onClose}>
      <h2 className="mb-3 text-lg font-medium">Поставил — куда?</h2>
      {unload ? (
        <p className="mb-3 text-sm opacity-70">Это байк с выгрузки — по умолчанию «На линию».</p>
      ) : null}
      <div className="flex flex-col gap-2">
        {DELIVER_RESULTS.map((result) => {
          const out = deliverPayload(result, me)
          return (
            <div key={result}>
              <BigButton
                tone={
                  result === 'broken'
                    ? 'danger'
                    : unload && result !== 'line'
                      ? 'neutral'
                      : 'primary'
                }
                className="w-full"
                disabled={!out.ok}
                onClick={() => {
                  if (out.ok) onDeliver(out.payload)
                }}
              >
                {deliverResultLabel[result]}
              </BigButton>
              <p className={`mt-1 text-xs ${out.ok ? 'opacity-60' : 'text-amber-200'}`}>
                {out.ok ? DELIVER_HINT[result] : out.error}
              </p>
            </div>
          )
        })}
        <BigButton tone="neutral" onClick={onClose}>
          Отмена
        </BigButton>
      </div>
    </BottomSheet>
  )
}

const DROP_REASONS: readonly DropReason[] = ['not_found', 'other']

/**
 * «Убрать из пула» — с подтверждением: байк возвращается в прежний статус,
 * а диспетчеру уходит сообщение. Нечаянное нажатие у соседнего байка
 * означало бы, что за ним никто не приедет.
 */
export function DropSheet({
  open,
  onClose,
  number,
  me,
  onDrop,
}: {
  open: boolean
  onClose: () => void
  number: string
  me: LatLng | null
  onDrop: (payload: Record<string, unknown>) => void
}) {
  const [reason, setReason] = useState<DropReason>('not_found')
  const [comment, setComment] = useState('')
  const [confirming, setConfirming] = useState(false)

  function close() {
    setReason('not_found')
    setComment('')
    setConfirming(false)
    onClose()
  }

  return (
    <BottomSheet open={open} onClose={close}>
      {confirming ? (
        <>
          <h2 className="mb-2 text-lg font-medium">Убрать №{number} из пула?</h2>
          <Notice tone="warn">
            Байк вернётся в прежний статус, диспетчер получит сообщение. Причина:{' '}
            {dropReasonLabel[reason].toLowerCase()}.
          </Notice>
          <div className="mt-3 flex gap-2">
            <BigButton
              tone="danger"
              onClick={() => {
                onDrop(dropPayload(reason, comment, me))
                close()
              }}
            >
              Да, убрать
            </BigButton>
            <BigButton
              tone="neutral"
              onClick={() => {
                setConfirming(false)
              }}
            >
              Назад
            </BigButton>
          </div>
        </>
      ) : (
        <>
          <h2 className="mb-2 text-lg font-medium">Убрать из пула</h2>
          <div className="flex gap-2">
            {DROP_REASONS.map((r) => (
              <button
                key={r}
                aria-pressed={reason === r}
                onClick={() => {
                  setReason(r)
                }}
                className={`min-h-11 flex-1 rounded-full px-4 text-sm font-medium transition ${
                  reason === r
                    ? 'bg-emerald-500 text-black'
                    : 'border border-white/20 text-white/80 active:bg-white/10'
                }`}
              >
                {dropReasonLabel[r]}
              </button>
            ))}
          </div>
          <label className="mt-3 block text-sm opacity-70" htmlFor="pool-drop-comment">
            Комментарий (необязательно)
          </label>
          <textarea
            id="pool-drop-comment"
            rows={2}
            maxLength={DROP_COMMENT_MAX}
            className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value)
            }}
            placeholder="Например: стоит во дворе за шлагбаумом"
          />
          <div className="mt-3 flex gap-2">
            <BigButton
              tone="danger"
              onClick={() => {
                setConfirming(true)
              }}
            >
              Убрать из пула
            </BigButton>
            <BigButton tone="neutral" onClick={close}>
              Отмена
            </BigButton>
          </div>
        </>
      )}
    </BottomSheet>
  )
}
